import { useState, useEffect } from 'react';
import { Plus, Search, Package, AlertTriangle } from 'lucide-react';
import useProductStore from '../../stores/useProductStore';
import ProductCard from '../../components/products/ProductCard';
import ProductModal from '../../components/products/ProductModal';

/**
 * Products Management Page
 * إدارة المنتجات والمخزون
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Component Structure
 * - P-STATE-001: Zustand State Management
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-SEARCH-001: Search & Filter
 * - P-UX-001: User Experience
 * - P-RESPONSIVE-001: Mobile First Design
 */
const Products = () => {
  const {
    products,
    getLowStockProducts,
    getCategories,
    searchProducts,
    getProductsByCategory,
    error,
    clearError
  } = useProductStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);

  // Get filtered products
  const getFilteredProducts = () => {
    if (showLowStockOnly) {
      return getLowStockProducts();
    }

    if (searchQuery) {
      return searchProducts(searchQuery);
    }

    if (selectedCategory !== 'all') {
      return getProductsByCategory(selectedCategory);
    }

    return products;
  };

  const filteredProducts = getFilteredProducts();
  const lowStockProducts = getLowStockProducts();
  const categories = ['all', ...getCategories()];

  // Handlers
  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  useEffect(() => {
    if (error) {
      setTimeout(() => clearError(), 5000);
    }
  }, [error, clearError]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              المنتجات
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              إدارة المنتجات والمخزون
            </p>
          </div>
          <button
            onClick={handleAddProduct}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            <span>منتج جديد</span>
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ابحث عن منتج..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pr-10 w-full"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="input sm:w-48"
          >
            <option value="all">كل الفئات</option>
            {categories.filter(cat => cat !== 'all').map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 pb-24">
        {/* Error Alert */}
        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Low Stock Alert */}
        {lowStockProducts.length > 0 && !showLowStockOnly && (
          <div className="bg-orange-100 dark:bg-orange-900/30 border border-orange-400 dark:border-orange-700 rounded-lg p-4 mb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-orange-900 dark:text-orange-200">
                    تنبيه: مخزون منخفض
                  </h3>
                  <p className="text-sm text-orange-800 dark:text-orange-300 mt-1">
                    يوجد {lowStockProducts.length} منتج بمخزون أقل من الحد الأدنى
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowLowStockOnly(true)}
                className="text-sm text-orange-700 dark:text-orange-300 hover:underline"
              >
                عرض المنتجات
              </button>
            </div>
          </div>
        )}

        {/* Low Stock Filter Badge */}
        {showLowStockOnly && (
          <div className="mb-4 flex items-center gap-2">
            <span className="badge badge-warning">
              عرض المنتجات منخفضة المخزون فقط
            </span>
            <button
              onClick={() => setShowLowStockOnly(false)}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              عرض الكل
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {products.length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  إجمالي المنتجات
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Package className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredProducts.length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  نتائج البحث
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {lowStockProducts.length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  مخزون منخفض
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Package className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {categories.length - 1}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  الفئات
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEditProduct}
              />
            ))}
          </div>
        ) : (
          <div className="card p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {searchQuery || selectedCategory !== 'all'
                ? 'لا توجد نتائج'
                : 'لا توجد منتجات'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery || selectedCategory !== 'all'
                ? 'جرب تغيير معايير البحث'
                : 'ابدأ بإضافة أول منتج'}
            </p>
            {!searchQuery && selectedCategory === 'all' && (
              <button onClick={handleAddProduct} className="btn btn-primary">
                <Plus className="w-5 h-5 ml-2" />
                إضافة منتج جديد
              </button>
            )}
          </div>
        )}
      </main>

      {/* Product Modal */}
      {showModal && (
        <ProductModal
          product={editingProduct}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Products;
